import { AccountSecurityViewModel } from "@bundle/core";
import { Icon, SecureAccountStep, SignupForm, Stack, Text, useLocale, useOwned, useSession, useT, VerifyPhoneStep } from "@bundle/ui";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { accountSecurityFor, takePhoneImportMarker } from "../platform/accountSecurity.ts";
import { isDesktop } from "../platform/desktop.ts";
import type { Container } from "../platform/container.ts";
import { settingsPlatform } from "../platform/settings/index.ts";
import { goTo, signupNext } from "../platform/signupRoute.ts";
import { LegalAgreement } from "../screens/LegalNotice.tsx";
import { AuthCard, AuthSwitch } from "../screens/SignInScreen.tsx";

/** /signup: Create your Bundle account (A name + email, D already registered, B email code). */
export function SignupScreen({ container }: { container: Container }) {
  const t = useT();
  const { vm, state } = useSession();
  // The code step is a page of its own (design B): its heading replaces this one, and no footer.
  const codeStep = state.signup?.step === "code" && state.emailStep === "code";
  useEffect(() => {
    const before = document.title;
    document.title = t("auth.signup.title");
    return () => {
      document.title = before;
    };
  }, [t]);
  const toSignIn = () => {
    vm.leaveSignup();
    goTo(signupNext(location.search));
  };
  return (
    <AuthCard testID="signup-screen">
      {codeStep ? null : (
        <Stack gap="1">
          <Text variant="title2" weight="bold">
            {t("auth.signup.title")}
          </Text>
          <Text color="text.secondary">{t("auth.signup.subtitle")}</Text>
        </Stack>
      )}
      <SignupForm returnTo={signupNext(location.search)} onSignIn={toSignIn} />
      {codeStep ? null : <LegalAgreement />}
      {codeStep ? null : <AuthSwitch question={t("auth.signup.haveAccount")} action={t("auth.signup.signIn")} onPress={toSignIn} testID="signup-to-sign-in" />}
      {container.mock ? (
        <Stack direction="horizontal" align="center" gap="2" justify="center">
          <Icon name="sparkles" size="icon.sm" color="text.tertiary" />
          <Text variant="caption" color="text.tertiary">
            {t("auth.mock")}
          </Text>
        </Stack>
      ) : null}
    </AuthCard>
  );
}

/**
 * Create account, right after the code and before the workspace flow: "Verify your phone" (where phone sign-in is on
 * and the account has no number yet; required when the email is hosted on Bundle), then C, "Secure your account"
 * (optional passkey or password).
 */
export function SecureAccountScreen({ container }: { container: Container }) {
  const { vm: session } = useSession();
  const locale = useLocale();
  const create = useCallback(
    () => new AccountSecurityViewModel({ security: accountSecurityFor(container), account: () => settingsPlatform(container).api.account(), locale }),
    [container, locale],
  );
  const vm = useOwned(`signup:secure:${session.state.session?.userId ?? ""}`, create);
  const state = useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot);
  useEffect(() => {
    void vm.load();
  }, [vm]);
  const done = useCallback(() => {
    session.finishSignup();
    goTo(signupNext(location.search), true);
  }, [session]);
  // Back from Google with the phone scope (read once).
  const [importNow] = useState(takePhoneImportMarker);
  const [phoneDone, setPhoneDone] = useState(false);
  const phoneDoneNow = useCallback(() => {
    vm.close();
    setPhoneDone(true);
  }, [vm]);
  if (state.status === "loading" && !phoneDone) return <AuthCard testID="signup-secure-screen">{null}</AuthCard>;
  if (!phoneDone && state.phoneSignIn && state.phone === undefined) {
    const google = state.account?.providers.includes("google") === true && !isDesktop();
    return (
      <AuthCard testID="signup-phone-screen">
        <VerifyPhoneStep security={{ vm, state }} onDone={phoneDoneNow} google={google} importNow={importNow} />
      </AuthCard>
    );
  }
  return (
    <AuthCard testID="signup-secure-screen">
      <SecureAccountStep
        security={{ vm, state }}
        passkeys={typeof window !== "undefined" && window.PublicKeyCredential !== undefined}
        onDone={done}
        onCopy={(text) => void navigator.clipboard?.writeText(text)}
      />
    </AuthCard>
  );
}
